import React, { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FieldHelpTooltip from "../../../../components/common/FieldHelpTooltip";
import AddressLookUp from "../../../components/AddressLookUp";
import { PartyAddress } from "../../../types/types";

interface AddressCardProps {
  partyId: number;
  addresses: PartyAddress[];
  onChange: (addresses: PartyAddress[]) => void;
  allowAddAnother?: boolean;
  allowDelete?: boolean;
}

interface AddressLookUpProps {
  sendAddressToParent: (data: any) => void;
  clearFromParent?: () => void;
}

type AddressFormState = {
  isPrimary: boolean;
  validFromUtc: string;
  validToUtc: string | null;
  address: {
    addressLine1: string;
    town: string;
    county: string;
    postCode: string;
    fullAddress: string;
  };
};

const createEmptyForm = (isPrimary = false): AddressFormState => ({
  isPrimary,
  validFromUtc: new Date().toISOString(),
  validToUtc: null,
  address: {
    addressLine1: "",
    town: "",
    county: "",
    postCode: "",
    fullAddress: "",
  },
});

const AddressCard: React.FC<AddressCardProps> = ({
  partyId,
  addresses,
  onChange,
  allowAddAnother = true,
  allowDelete = true,
}) => {
  const childSearchAddressRef = useRef<AddressLookUpProps>(null);

  const isNew = addresses.length === 0;
  const isSingle = addresses.length === 1;
  const isMultiple = addresses.length > 1;

  const [isAdding, setIsAdding] = useState(addresses.length === 0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formState, setFormState] = useState<AddressFormState>(
    createEmptyForm(true),
  );

  const isEditingMultiple = isMultiple && editingId != null;
  const isAddingAdditional =
    isAdding && editingId == null && addresses.length > 0;

  const isFormComplete =
    formState.address.addressLine1.trim() !== "" &&
    formState.address.postCode.trim() !== "";

  const getAddressSummary = (addr: PartyAddress) =>
    [
      addr.address.addressLine1,
      addr.address.town,
      addr.address.county,
      addr.address.postCode,
    ]
      .map((part) => part?.trim())
      .filter((part) => !!part)
      .join(", ");

  useEffect(() => {
    if (addresses.length === 0) {
      // New party
      setIsAdding(true);
      setEditingId(null);
      setFormState(createEmptyForm(true));
      return;
    }

    if (addresses.length === 1) {
      // Single address → inline edit mode
      const addr = addresses[0];

      setIsAdding(false);
      setEditingId(addr.partyAddressId);

      setFormState({
        isPrimary: addr.isPrimary,
        validFromUtc: addr.validFromUtc,
        validToUtc: addr.validToUtc,
        address: {
          addressLine1: addr.address.addressLine1 ?? "",
          town: addr.address.town ?? "",
          county: addr.address.county ?? "",
          postCode: addr.address.postCode ?? "",
          fullAddress: addr.address.fullAddress ?? "",
        },
      });

      return;
    }

    // THIS IS THE IMPORTANT PART
    if (addresses.length > 1) {
      // Multiple → LIST VIEW ONLY
      setIsAdding(false);
      setEditingId(null);
      setFormState(createEmptyForm(false));
    }
  }, [addresses]);

  const buildUpdatedForm = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ): AddressFormState => {
    const { name, value } = e.target;

    if (name in formState.address) {
      return {
        ...formState,
        address: {
          ...formState.address,
          [name]: value,
        },
      };
    }

    if (name === "isPrimary") {
      return {
        ...formState,
        isPrimary: (e.target as HTMLInputElement).checked,
      };
    }

    return formState;
  };

  const upsertSingleAddress = (updatedForm: AddressFormState) => {
    const updatedAddress: PartyAddress = {
      partyAddressId: addresses[0]?.partyAddressId ?? 0,
      partyId,
      isPrimary: updatedForm.isPrimary,
      validFromUtc: updatedForm.validFromUtc,
      validToUtc: updatedForm.validToUtc,
      address: {
        addressId: addresses[0]?.address?.addressId ?? 0,
        addressLine1: updatedForm.address.addressLine1,
        town: updatedForm.address.town,
        county: updatedForm.address.county,
        postCode: updatedForm.address.postCode,
        fullAddress: updatedForm.address.fullAddress,
      },
    };

    onChange([updatedAddress]);
  };

  const appendAddress = (updatedForm: AddressFormState) => {
    const nextAddress: PartyAddress = {
      partyAddressId: 0,
      partyId,
      isPrimary: formState.isPrimary || addresses.length === 0,
      validFromUtc: updatedForm.validFromUtc,
      validToUtc: updatedForm.validToUtc,
      address: {
        addressId: 0,
        addressLine1: updatedForm.address.addressLine1,
        town: updatedForm.address.town,
        county: updatedForm.address.county,
        postCode: updatedForm.address.postCode,
        fullAddress: updatedForm.address.fullAddress,
      },
    };

    const updated = [
      ...addresses.map((a) =>
        nextAddress.isPrimary ? { ...a, isPrimary: false } : a,
      ),
      nextAddress,
    ];

    onChange(updated);
  };

  const handleAutoSave = (updatedForm: AddressFormState) => {
    if (!isFormCompleteFor(updatedForm)) return;

    if (addresses.length === 0) {
      appendAddress(updatedForm);
      setFormState(createEmptyForm(false));
      childSearchAddressRef.current?.clearFromParent?.();
      return;
    }

    if (addresses.length === 1) {
      if (isAddingAdditional) {
        appendAddress(updatedForm);
        setFormState(createEmptyForm(false));
        childSearchAddressRef.current?.clearFromParent?.();
      } else {
        upsertSingleAddress(updatedForm);
      }
    }
  };

  const isFormCompleteFor = (state: AddressFormState) =>
    state.address.addressLine1.trim() !== "" &&
    state.address.postCode.trim() !== "";

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const updatedForm = buildUpdatedForm(e);
    setFormState(updatedForm);

    if (!isMultiple && !isAddingAdditional) {
      handleAutoSave(updatedForm);
    }
  };

  const setAddressFromChild = (data: any) => {
    const updatedForm: AddressFormState = {
      ...formState,
      address: {
        ...formState.address,
        addressLine1: data.addressLine1 || "",
        town: data.postTown || "",
        county: data.county || "",
        postCode: data.postCode || "",
        fullAddress: data.fullAddress || "",
      },
    };

    setFormState(updatedForm);

    if (!isMultiple && !isAddingAdditional) {
      handleAutoSave(updatedForm);
    }
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormState(createEmptyForm(false));
    childSearchAddressRef.current?.clearFromParent?.();
  };

  const startEdit = (addr: PartyAddress) => {
    setEditingId(addr.partyAddressId);
    setIsAdding(false);
    setFormState({
      isPrimary: addr.isPrimary,
      validFromUtc: addr.validFromUtc,
      validToUtc: addr.validToUtc,
      address: {
        addressLine1: addr.address.addressLine1 ?? "",
        town: addr.address.town ?? "",
        county: addr.address.county ?? "",
        postCode: addr.address.postCode ?? "",
        fullAddress: addr.address.fullAddress ?? "",
      },
    });
  };

  const cancelEdit = () => {
    childSearchAddressRef.current?.clearFromParent?.();

    if (addresses.length === 0) {
      // New party → stay in empty form
      setIsAdding(true);
      setEditingId(null);
      setFormState(createEmptyForm(true));
      return;
    }

    if (addresses.length === 1) {
      // Single address → go back to edit mode
      const addr = addresses[0];

      setIsAdding(false);
      setEditingId(addr.partyAddressId);

      setFormState({
        isPrimary: addr.isPrimary,
        validFromUtc: addr.validFromUtc,
        validToUtc: addr.validToUtc,
        address: {
          addressLine1: addr.address.addressLine1 ?? "",
          town: addr.address.town ?? "",
          county: addr.address.county ?? "",
          postCode: addr.address.postCode ?? "",
          fullAddress: addr.address.fullAddress ?? "",
        },
      });

      return;
    }

    // Multiple → back to list view
    setIsAdding(false);
    setEditingId(null);
    setFormState(createEmptyForm(false));
  };

  const saveMultipleAddress = () => {
    if (!isFormComplete) {
      alert("Please fill in required fields");
      return;
    }

    let updated: PartyAddress[];

    if (isAddingAdditional) {
      updated = [
        ...addresses.map((a) =>
          formState.isPrimary ? { ...a, isPrimary: false } : a,
        ),
        {
          partyAddressId: 0,
          partyId,
          isPrimary: formState.isPrimary,
          validFromUtc: formState.validFromUtc,
          validToUtc: formState.validToUtc,
          address: {
            addressId: 0,
            addressLine1: formState.address.addressLine1,
            town: formState.address.town,
            county: formState.address.county,
            postCode: formState.address.postCode,
            fullAddress: formState.address.fullAddress,
          },
        },
      ];
    } else if (isEditingMultiple) {
      updated = addresses.map((a) => {
        if (a.partyAddressId === editingId) {
          return {
            ...a,
            isPrimary: formState.isPrimary,
            validFromUtc: formState.validFromUtc,
            validToUtc: formState.validToUtc,
            address: {
              ...a.address,
              addressLine1: formState.address.addressLine1,
              town: formState.address.town,
              county: formState.address.county,
              postCode: formState.address.postCode,
              fullAddress: formState.address.fullAddress,
            },
          };
        }

        if (formState.isPrimary) {
          return { ...a, isPrimary: false };
        }

        return a;
      });
    } else {
      return;
    }

    onChange(updated);
    setEditingId(null);
    setIsAdding(false);
    setFormState(createEmptyForm(false));
    childSearchAddressRef.current?.clearFromParent?.();
  };

  const deleteAddress = (id: number) => {
    if (!window.confirm("Delete this address?")) return;

    const remaining = addresses.filter((a) => a.partyAddressId !== id);

    if (remaining.length > 0 && !remaining.some((a) => a.isPrimary)) {
      remaining[0] = { ...remaining[0], isPrimary: true };
    }

    onChange(remaining);
  };

  const disableAddAnother =
    addresses.length === 0 && !isFormCompleteFor(formState);

  const showList = isMultiple && !isAdding && editingId == null;
  const showForm = isNew || isSingle || isAdding || editingId != null;

  return (
    <div className="card">
      <div className="card-header party-card-header">
        <div className="card-header-row party-card-header-row">
          <div className="party-card-title-group">
            <div className="card-title">
              <span>Addresses</span>
              <FieldHelpTooltip text="Add every address the party uses." />
            </div>
          </div>

          {allowAddAnother && (
            <button
              type="button"
              className="link-button link-button-with-icon card-header-link-action"
              onClick={startAdd}
              disabled={disableAddAnother}
            >
              <AddIcon fontSize="small" />
              Add Another
            </button>
          )}
        </div>
      </div>

      {showList && (
        <div className="address-list" role="list">
          {addresses.map((addr, index) => (
            <div
              key={
                addr.partyAddressId > 0
                  ? `address-${addr.partyAddressId}`
                  : `address-new-${index}-${addr.address.addressLine1}-${addr.address.postCode}`
              }
              className="address-list-row"
              role="listitem"
            >
              <div className="address-list-body">
                <p className="address-list-value">{getAddressSummary(addr)}</p>
              </div>

              <div className="address-list-actions">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => startEdit(addr)}
                  aria-label="Edit address"
                >
                  <EditIcon fontSize="small" />
                </button>
                {allowDelete && (
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => deleteAddress(addr.partyAddressId)}
                    aria-label="Delete address"
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <>
          <AddressLookUp
            ref={childSearchAddressRef}
            sendAddressToParent={setAddressFromChild}
          />

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Address Line 1</label>
              <input
                name="addressLine1"
                className="form-input"
                value={formState.address.addressLine1}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Town</label>
              <input
                name="town"
                className="form-input"
                value={formState.address.town}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">County</label>
              <input
                name="county"
                className="form-input"
                value={formState.address.county}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Post Code</label>
              <input
                name="postCode"
                className="form-input"
                value={formState.address.postCode}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* <div className="toggle-container">
            <span className="toggle-label-text">Primary</span>
            <div className="toggle-wrapper">
              <input
                type="checkbox"
                name="isPrimary"
                checked={formState.isPrimary}
                onChange={handleChange}
                className="toggle-checkbox"
              />
              <span className="toggle-background" />
            </div>
          </div> */}

          {(isEditingMultiple || isAddingAdditional) && (
            <div className="form-row">
              <div className="button-row button-row-left">
                <button
                  type="button"
                  className="action-button"
                  onClick={saveMultipleAddress}
                >
                  Confirm
                </button>

                <button
                  type="button"
                  className="action-button secondary"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AddressCard;
